﻿import { KbColor } from '@models/classes/primitives/kb-color';
import { isTrackedClass } from '@models/interfaces/tracked-class';
import { IgnoreHistory } from '../decorators/change-tracker';
import { BaseKb3dModel } from '../decorators/kb3d-decorator';
import { instanceOfAny } from '../decorators/model-utils';
import {
    eTextureBlendMode,
    eTextureChannel,
    eTextureFit,
    eTextureHorizontalAlign,
    eTextureVerticalAlign,
    eTextureWrapAddressMode,
} from '../enums';
import { FeatureWithTextures } from '../features/feature';
import { ITextureLayer } from '../kb3d-models';
import { Kb3dObject } from '../kb3d-object';
import { Token } from '../token';
import { MaterialMapping } from './material-mapping';
import { MaterialNode } from './material-node';

@BaseKb3dModel<TextureLayer>({
    token: Token.TextureLayer,
})
export abstract class TextureLayer extends MaterialMapping implements ITextureLayer {
    channel: eTextureChannel;
    blendMode: eTextureBlendMode;
    horizontalAlign: eTextureHorizontalAlign;
    verticalAlign: eTextureVerticalAlign;
    horizontalFit: eTextureFit;
    verticalFit: eTextureFit;
    strength: number;
    colorize?: KbColor;
    tile: boolean;
    uWrap: eTextureWrapAddressMode;
    vWrap: eTextureWrapAddressMode;
    maintainAspect: boolean;
    _visualize: boolean;
    useClippingMask: boolean;

    @IgnoreHistory
    _init = false;

    @IgnoreHistory
    _external = false;

    enabled: boolean;
    _suppressed: boolean;

    /** override base parent for more specific typing*/
    public _parent: MaterialNode | FeatureWithTextures | undefined;

    init(parent: MaterialNode | FeatureWithTextures) {
        if (!this._init) {
            this._parent = parent;
            // Normally this function is called when the node is added to an array on the parent, but textures live in defined properties on a material.
            // So we have to explicitly call it here, on texture init. The texture won't be doing anything before init anyways.
            this.calculateParents();
            if (isTrackedClass(this)) {
                // Always track changes on initialized textures because they may be attached to global materials
                this.trackChanges(true);
                this._init = true;
            }
        }
    }
}

export function isTextureLayer(node: Kb3dObject): node is TextureLayer {
    return instanceOfAny(node, ...Token.TextureLayerTokens);
}
